import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { Level } from "building-types/levels/levelTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import { getWallPath } from "building-types/walls/wallUtils";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { resolveContentSourceIdToLevelIds } from "doc/docViewport";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { uniqueRoomSetsTables } from "doc/rooms/uniqueRoomSetsTables";
import { Line2CollinearOverlap } from "geom/core";
import { line2CollinearOverlap } from "geom/line2";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine2 } from "geom/polyLine2Types";

const VOID_WALL_SEGMENT_CONTAINMENT_TOLERANCE = 1e-3;

export interface InteriorDimensionRoomGroupData {
  memberLevelIds: string[];
  roomSets: RoomSet[];
  levelRooms: Room[];
  levelRoomsByLevel: Table<Room[]>;
}

export interface RoomSetByMemberLevel {
  levelId: string;
  roomSet: RoomSet;
  voidWallSegments: PolyLine2[];
}

export function collectCompositeInteriorDimensionRoomGroups(
  building: Pick<Building, "rooms" | "roomsByLevel" | "wallMasses" | "walls" | "dimensionBehavior" | "levels"> & {
    doc: Pick<Doc, "compositeLevels" | "levels">;
  },
  levelId: string,
): InteriorDimensionRoomGroupData {
  const candidateLevelIds = resolveContentSourceIdToLevelIds(building.doc, levelId, building.levels);
  const memberLevelIds = candidateLevelIds.filter((memberLevelId) => {
    return (
      building.roomsByLevel[memberLevelId] !== undefined &&
      building.wallMasses[memberLevelId] !== undefined &&
      building.walls[memberLevelId] !== undefined &&
      building.dimensionBehavior[memberLevelId] !== undefined
    );
  });

  const levelRoomsByLevel: RWTable<Room[]> = {};
  const levelRooms: Room[] = [];

  const compositeLevels: Table<Level> = Object.fromEntries(
    memberLevelIds.map((memberLevelId) => [memberLevelId, building.levels[memberLevelId]]),
  );
  const { roomSets } = uniqueRoomSetsTables(compositeLevels, building.wallMasses);
  const roomSetsByMemberLevel: RoomSetByMemberLevel[] = roomSets.map(({ levelId, roomSet }) => {
    const wallMassSystem = building.wallMasses[levelId];
    assertIsNotNullOrUndefined(wallMassSystem);
    return {
      levelId,
      roomSet,
      voidWallSegments: collectVoidWallSegments(roomSet, building.rooms, building.wallMasses[levelId].wallIndex),
    };
  });

  for (const memberLevelId of memberLevelIds) {
    const memberLevelRooms = roomsOnLevel(memberLevelId, building.rooms, building.roomsByLevel);
    levelRoomsByLevel[memberLevelId] = memberLevelRooms;
    levelRooms.push(...memberLevelRooms);
  }

  const mergedRoomSets = mergeRoomSetsAcrossCompositeLevels(roomSetsByMemberLevel);

  return {
    memberLevelIds,
    roomSets: mergedRoomSets,
    levelRooms,
    levelRoomsByLevel,
  };
}

function collectVoidWallSegments(roomSet: RoomSet, rooms: Table<Room>, wallIndex: WallIndex): PolyLine2[] {
  const segments: PolyLine2[] = [];
  const visitedWallIds = new Set<string>();

  for (const roomLike of roomSet) {
    const room = rooms[roomLike.id];
    if (!room) {
      continue;
    }

    for (const wall of wallIndex.wallsInRoom(room)) {
      if (wall.wallType !== WallType.VOID) {
        continue;
      }

      if (visitedWallIds.has(wall.id)) {
        continue;
      }

      visitedWallIds.add(wall.id);
      const path = getWallPath(wall);
      segments.push(polyLine2New([path.startX, path.startY, path.endX, path.endY]));
    }
  }

  return segments;
}

function roomSetsShareVoidWallSegment(a: PolyLine2[], b: PolyLine2[]): boolean {
  if (a.length === 0 || b.length === 0) {
    return false;
  }

  for (const segmentA of a) {
    for (const segmentB of b) {
      if (voidWallSegmentsOverlap(segmentA, segmentB)) {
        return true;
      }
    }
  }

  return false;
}

function voidWallSegmentsOverlap(segmentA: PolyLine2, segmentB: PolyLine2): boolean {
  const [ax0, ay0, ax1, ay1] = segmentA;
  const [bx0, by0, bx1, by1] = segmentB;
  const tolSqrd = VOID_WALL_SEGMENT_CONTAINMENT_TOLERANCE * VOID_WALL_SEGMENT_CONTAINMENT_TOLERANCE;
  const overlap = line2CollinearOverlap(
    ax0,
    ay0,
    ax1,
    ay1,
    bx0,
    by0,
    bx1,
    by1,
    false,
    tolSqrd,
    VOID_WALL_SEGMENT_CONTAINMENT_TOLERANCE,
  );
  return overlap !== Line2CollinearOverlap.NONE && overlap !== Line2CollinearOverlap.POINT;
}

function mergeRoomSetsAcrossCompositeLevels(roomSetsByMemberLevel: RoomSetByMemberLevel[]): RoomSet[] {
  if (roomSetsByMemberLevel.length <= 1) {
    return roomSetsByMemberLevel.map(({ roomSet }) => roomSet);
  }

  const parentByIndex = roomSetsByMemberLevel.map((_roomSet, index) => index);

  const findRoot = (index: number): number => {
    let root = index;
    while (parentByIndex[root] !== root) {
      parentByIndex[root] = parentByIndex[parentByIndex[root]];
      root = parentByIndex[root];
    }
    return root;
  };

  const unionRoots = (a: number, b: number): void => {
    const rootA = findRoot(a);
    const rootB = findRoot(b);
    if (rootA !== rootB) {
      parentByIndex[rootB] = rootA;
    }
  };

  for (let i = 0; i < roomSetsByMemberLevel.length; i++) {
    for (let j = i + 1; j < roomSetsByMemberLevel.length; j++) {
      if (roomSetsByMemberLevel[i].levelId === roomSetsByMemberLevel[j].levelId) {
        continue;
      }

      if (
        roomSetsShareVoidWallSegment(
          roomSetsByMemberLevel[i].voidWallSegments,
          roomSetsByMemberLevel[j].voidWallSegments,
        )
      ) {
        unionRoots(i, j);
      }
    }
  }

  const mergedRoomSetsByRoot = new Map<number, { roomSet: RoomSet; roomIds: Set<string> }>();

  for (let index = 0; index < roomSetsByMemberLevel.length; index++) {
    const root = findRoot(index);
    const roomSet = roomSetsByMemberLevel[index].roomSet;
    let rootEntry = mergedRoomSetsByRoot.get(root);

    if (!rootEntry) {
      rootEntry = {
        roomSet: [],
        roomIds: new Set<string>(),
      };
      mergedRoomSetsByRoot.set(root, rootEntry);
    }

    for (const room of roomSet) {
      if (rootEntry.roomIds.has(room.id)) {
        continue;
      }

      rootEntry.roomIds.add(room.id);
      rootEntry.roomSet.push(room);
    }
  }

  return Array.from(mergedRoomSetsByRoot.values(), ({ roomSet }) => roomSet);
}
